import {
  BdcCreditableItem,
  BdcCreditNoteDetail,
  BdcCreditNoteSaveRequest,
  BdcCreditNoteStatus,
  BdcCreditNoteSummary,
  BdcMoneyTotals,
} from "../../../types/bdc.type";
import { roundHalfUp } from "../../../utils/money";
import { BDC_RETURN_DOC_NO_LENGTH } from "../constants";
import { TagTone } from "../styles";

/** VAT on a CN line — 7 %, rounded half-up per line (spec 3.1); the head is the sum of the lines. */
export const CN_VAT_RATE = 0.07;

export const CN_STATUS: Record<BdcCreditNoteStatus, { label: string; tone: TagTone }> = {
  draft: { label: "ร่าง", tone: "mut" },
  issued: { label: "ออกแล้ว", tone: "ok" },
  queued_7_11: { label: "ส่ง 7-11 แล้ว", tone: "info" },
  cancelled: { label: "ยกเลิก", tone: "crit" },
};

/** One line of the CN form while it is edited. `qty` is the typed text. */
export interface CnDraftLine {
  /** `${invoice_no}|${barcode}` — one line per INV × product. */
  key: string;
  invoice_no: string;
  po_number: string;
  ref_no: string;
  barcode: string;
  item_code: string;
  name: string;
  size: string | null;
  unit_price: number;
  qty: string;
  invoiced_qty: number | null;
  /**
   * What this line may still take: the server's creditable_qty plus what this draft itself
   * already holds (the server counts drafts among the CNs that are not cancelled). null = unknown
   * (the line came from a saved draft and the INV has not been looked up).
   */
  available: number | null;
}

export interface CnDraftHeader {
  dc_code: string;
  return_doc_no: string;
  note_date: string;
  reason: string;
}

export const lineKey = (invoiceNo: string, barcode: string) => `${invoiceNo}|${barcode}`;

/** The typed quantity as a whole number of pieces, or null while it is not one (> 0). */
export const parseQty = (text: string): number | null => {
  const cleaned = text.replace(/[\s,]/g, "");
  if (!/^\d+$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return value > 0 ? value : null;
};

export const cnLineTotal = (qty: number, unitPrice: number): number => roundHalfUp(qty * unitPrice, 2);
export const cnLineVat = (lineTotal: number): number => roundHalfUp(lineTotal * CN_VAT_RATE, 2);

/** The head totals as the server computes them: Σ line_total, Σ per-line VAT. Bad quantities count 0. */
export const cnTotals = (lines: Pick<CnDraftLine, "qty" | "unit_price">[]): BdcMoneyTotals => {
  let subtotal = 0;
  let vat = 0;
  for (const line of lines) {
    const qty = parseQty(line.qty);
    if (qty === null) continue;
    const total = cnLineTotal(qty, line.unit_price);
    subtotal += total;
    vat += cnLineVat(total);
  }
  subtotal = roundHalfUp(subtotal, 2);
  vat = roundHalfUp(vat, 2);
  return { subtotal, vat_amount: vat, grand_total: roundHalfUp(subtotal + vat, 2) };
};

/** Lines asking for more than the INV has left (a warning on the draft; issuing refuses it). */
export const overCreditedLines = (lines: CnDraftLine[]): CnDraftLine[] =>
  lines.filter((line) => {
    const qty = parseQty(line.qty);
    return qty !== null && line.available !== null && qty > line.available;
  });

/** A new line from one product of an INV's creditable list. */
export const lineFromCreditable = (
  invoice: { invoice_no: string; po_number: string },
  item: BdcCreditableItem,
  qty: number,
): CnDraftLine => ({
  key: lineKey(invoice.invoice_no, item.barcode),
  invoice_no: invoice.invoice_no,
  po_number: invoice.po_number,
  ref_no: invoice.po_number,
  barcode: item.barcode,
  item_code: item.item_code,
  name: item.name,
  size: item.size,
  unit_price: item.unit_price,
  qty: String(qty),
  invoiced_qty: item.invoiced_qty,
  available: item.creditable_qty,
});

/** The lines of a saved CN, for editing (availability is filled in once each INV is looked up). */
export const linesFromDetail = (detail: BdcCreditNoteDetail): CnDraftLine[] =>
  [...detail.items]
    .sort((a, b) => a.line_no - b.line_no)
    .map((item) => ({
      key: lineKey(item.invoice_no, item.barcode),
      invoice_no: item.invoice_no,
      po_number: item.po_number,
      ref_no: item.ref_no || item.po_number,
      barcode: item.barcode,
      item_code: item.item_code,
      name: item.name,
      size: item.size,
      unit_price: item.unit_price,
      qty: String(item.qty),
      invoiced_qty: null,
      available: null,
    }));

/**
 * Fill each line's availability from an INV's creditable list. `savedQty` is what the stored
 * draft holds per line key — the server already counts it as credited, so it is given back.
 */
export const withAvailability = (
  lines: CnDraftLine[],
  invoiceNo: string,
  items: BdcCreditableItem[],
  savedQty: Readonly<Record<string, number>>,
): CnDraftLine[] =>
  lines.map((line) => {
    if (line.invoice_no !== invoiceNo) return line;
    const item = items.find((i) => i.barcode === line.barcode);
    if (!item) return line;
    return {
      ...line,
      invoiced_qty: item.invoiced_qty,
      available: item.creditable_qty + (savedQty[line.key] ?? 0),
    };
  });

/** Why the draft cannot be saved yet, or null. */
export const draftProblem = (header: CnDraftHeader, lines: CnDraftLine[]): string | null => {
  if (!header.dc_code) return "เลือก DC";
  const returnNo = header.return_doc_no.trim();
  if (!returnNo) return "กรอกเลขเอกสารคืนของ 7-11";
  if (!/^\d+$/.test(returnNo)) return "เลขเอกสารคืนต้องเป็นตัวเลข";
  if (returnNo.length !== BDC_RETURN_DOC_NO_LENGTH) return `เลขเอกสารคืนต้องมี ${BDC_RETURN_DOC_NO_LENGTH} หลัก`;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(header.note_date)) return "เลือกวันที่ CN";
  if (!header.reason.trim()) return "กรอกเหตุผล";
  if (lines.length === 0) return "เพิ่มสินค้าอย่างน้อย 1 รายการ";
  const bad = lines.find((line) => parseQty(line.qty) === null);
  if (bad) return `จำนวนของ ${bad.item_code} (${bad.invoice_no}) ต้องเป็นจำนวนเต็มมากกว่า 0`;
  return null;
};

/** The POST / PUT body; ref_no is left out where it is still the PO. */
export const saveBody = (header: CnDraftHeader, lines: CnDraftLine[]): BdcCreditNoteSaveRequest => ({
  dc_code: header.dc_code,
  return_doc_no: header.return_doc_no.trim(),
  note_date: header.note_date,
  reason: header.reason.trim(),
  items: lines.map((line) => {
    const ref = line.ref_no.trim();
    return {
      invoice_no: line.invoice_no,
      barcode: line.barcode,
      qty: parseQty(line.qty) ?? 0,
      ...(ref && ref !== line.po_number ? { ref_no: ref } : {}),
    };
  }),
});

/** A CN can go into a RETSEV file once issued (a real number, not yet in a file). */
export const canExportRetsev = (note: Pick<BdcCreditNoteSummary, "status" | "is_test">): boolean =>
  note.status === "issued" && !note.is_test;

/** ยกเลิก CN: issued, not in a RETSEV file and not on a ใบวางบิล. */
export const canCancelNote = (note: Pick<BdcCreditNoteSummary, "status" | "billing_uuid">): boolean =>
  note.status === "issued" && !note.billing_uuid;

/** Why a ticked set of CNs cannot go into a RETSEV file, or null. */
export const retsevProblem = (notes: BdcCreditNoteSummary[], max: number): string | null => {
  if (notes.length === 0) return "เลือก CN อย่างน้อย 1 ใบ";
  if (notes.length > max) return `เลือกได้ครั้งละไม่เกิน ${max} ใบ`;
  const misfit = notes.filter((note) => !canExportRetsev(note));
  if (misfit.length > 0) return `CN ${misfit.map((n) => n.note_no ?? "ร่าง").join(", ")} สร้างไฟล์ไม่ได้ (ต้องออกแล้ว และไม่ใช่ชุดทดสอบ)`;
  return null;
};
